import { createSequence } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import {
  AdListItem,
  AdListItemSkeleton,
} from '~/components/molecules/AdListItem';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Pagination } from '~/components/molecules/Pagination';
import { Table } from '~/components/molecules/Table';
import { usePagination } from '~/hooks/usePagination';
import { useAdsQuery } from '~/lib/api/modules/Ads/hooks/useAdsQuery';

const GRID_TEMPLATE_COLUMNS = 'repeat(3, 1fr) repeat(1, min-content)';
const DEFAULT_SKELETON_COUNT = 4;

export type IAdListProps = IContainerProps & {
  appId: string;
};

export const AdList: React.FC<IAdListProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const pagination = usePagination();
  const adsQuery = useAdsQuery(
    {
      appId,
      body: {
        start: pagination.start,
        limit: pagination.limit,
      },
    },
    {
      enabled:
        Number.isInteger(pagination.start) &&
        Number.isInteger(pagination.limit),
      keepPreviousData: true,
    },
  );

  const ads = adsQuery.data;
  const isEmpty = !adsQuery.isLoading && !ads?.list?.length;

  return (
    <ContentBlock sx={sx}>
      {adsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={adsQuery.error} />
        </ContentBlock.Body>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('AdList.empty.title')}>
          <Button href={`/apps/${appId}/ads/create`}>
            {t('AdList.empty.actionLabel')}
          </Button>
        </ContentBlock.Empty>
      ) : (
        <>
          <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
            {adsQuery.isLoading || adsQuery.isPreviousData
              ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                  <AdListItemSkeleton key={index} first={index === 0} />
                ))
              : ads?.list?.map((liveStream, liveStreamIndex) => (
                  <AdListItem
                    key={liveStream._id}
                    ad={liveStream}
                    first={liveStreamIndex === 0}
                  />
                ))}
          </Table>

          <ContentBlock.Footer>
            <Pagination pagination={pagination} total={adsQuery.data?.count} />
          </ContentBlock.Footer>
        </>
      )}
    </ContentBlock>
  );
};
